import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'

const INDUSTRIES = ['Home services', 'Professional services', 'Agency', 'E-commerce', 'SaaS', 'Other']

const ALL_MODULES = [
  { id: 'Inbox',              desc: 'Customer conversations & support' },
  { id: 'CRM',                desc: 'Contacts, companies & deals' },
  { id: 'Quotes & Invoices',  desc: 'Billing and payments' },
  { id: 'Projects',           desc: 'Track work and deliverables' },
  { id: 'Field',              desc: 'Dispatch and field service jobs' },
  { id: 'Reputation',         desc: 'Reviews and customer feedback' },
  { id: 'Marketing',          desc: 'Campaigns and outreach' },
]

const TEAM_SIZES = [
  { id: 'just_me', label: 'Just me' },
  { id: '2-5',     label: '2–5 people' },
  { id: '6-20',    label: '6–20 people' },
  { id: '21+',     label: '21+ people' },
]

const PRESET_COLORS = ['#7c3aed', '#0c87de', '#00d9a3', '#ff6b6b', '#fbbf24', '#ec4899']

async function seedSampleData(workspaceId: string) {
  const contacts = [
    { workspace_id: workspaceId, first_name: 'Sarah',  last_name: 'Johnson',   email: 'sarah.johnson@apexcorp.com',  phone: '+1 555 0101', status: 'active' as const, company_name: 'Apex Corp'     },
    { workspace_id: workspaceId, first_name: 'Marcus', last_name: 'Chen',      email: 'marcus@brightwave.io',        phone: '+1 555 0102', status: 'lead'   as const, company_name: 'Brightwave'    },
    { workspace_id: workspaceId, first_name: 'Elena',  last_name: 'Rodriguez', email: 'elena@solarpeak.com',         phone: '+1 555 0103', status: 'active' as const, company_name: 'Solar Peak'    },
    { workspace_id: workspaceId, first_name: 'James',  last_name: 'Okafor',   email: 'james@nexustech.co',          phone: '+1 555 0104', status: 'lead'   as const, company_name: 'Nexus Tech'    },
    { workspace_id: workspaceId, first_name: 'Priya',  last_name: 'Patel',    email: 'priya@bloomdesign.com',       phone: '+1 555 0105', status: 'active' as const, company_name: 'Bloom Design'  },
  ]
  const deals = [
    { workspace_id: workspaceId, title: 'Apex Corp – Annual Contract', value: 24000, currency: 'USD', status: 'open' as const, stage_name: 'Proposal',    contact_name: 'Sarah Johnson'  },
    { workspace_id: workspaceId, title: 'Brightwave – Starter Plan',   value:  4800, currency: 'USD', status: 'open' as const, stage_name: 'Qualified',   contact_name: 'Marcus Chen'    },
    { workspace_id: workspaceId, title: 'Solar Peak – Consulting',     value:  8500, currency: 'USD', status: 'open' as const, stage_name: 'Negotiation', contact_name: 'Elena Rodriguez' },
  ]
  const conversations = [
    { workspace_id: workspaceId, contact_name: 'Sarah Johnson', subject: 'Contract renewal questions',   status: 'open' as const, priority: 'high'   as const, channel: 'email' as const, unread_count: 3 },
    { workspace_id: workspaceId, contact_name: 'Marcus Chen',   subject: 'Onboarding support request',  status: 'open' as const, priority: 'normal' as const, channel: 'chat'  as const, unread_count: 1 },
  ]
  await Promise.all([
    supabase.from('contacts').insert(contacts),
    supabase.from('deals').insert(deals),
    supabase.from('conversations').insert(conversations),
  ])
}

const slideVariants = {
  enter:  (dir: number) => ({ x: dir > 0 ?  56 : -56, opacity: 0 }),
  center:              () => ({ x: 0,                   opacity: 1 }),
  exit:   (dir: number) => ({ x: dir > 0 ? -56 :  56, opacity: 0 }),
}

// ─── Shared style tokens ────────────────────────────────────────────────────

const headingStyle: React.CSSProperties = {
  fontSize: 22, fontWeight: 700, letterSpacing: '-0.04em', color: 'var(--ink-primary)', margin: 0,
}
const subStyle: React.CSSProperties = {
  fontSize: 14, color: 'var(--ink-tertiary)', lineHeight: '1.55', margin: '8px 0 0',
}
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)',
  letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 7,
}
const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '10px 13px',
  background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)',
  borderRadius: 10, color: 'var(--ink-primary)', fontSize: 14, outline: 'none',
}

// ─── Step components ─────────────────────────────────────────────────────────

function StepWelcome({ workspaceName, setWorkspaceName, industry, setIndustry }: {
  workspaceName: string; setWorkspaceName: (v: string) => void
  industry: string; setIndustry: (v: string) => void
}) {
  const [nameFocused, setNameFocused]     = useState(false)
  const [indFocused,  setIndFocused]      = useState(false)
  return (
    <div>
      <h2 style={headingStyle}>Welcome to Orkestra</h2>
      <p style={subStyle}>Let's set up your workspace in a few quick steps.</p>
      <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <div>
          <label style={labelStyle}>Workspace name</label>
          <input
            style={{ ...inputStyle, borderColor: nameFocused ? 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)' : 'var(--border-subtle)' }}
            value={workspaceName}
            onChange={e => setWorkspaceName(e.target.value)}
            onFocus={() => setNameFocused(true)}
            onBlur={() => setNameFocused(false)}
            placeholder="Acme Co"
          />
        </div>
        <div>
          <label style={labelStyle}>Industry</label>
          <select
            style={{
              ...inputStyle,
              borderColor: indFocused ? 'color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)' : 'var(--border-subtle)',
              appearance: 'none', cursor: 'pointer',
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='var(--ink-tertiary)' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
            }}
            value={industry}
            onChange={e => setIndustry(e.target.value)}
            onFocus={() => setIndFocused(true)}
            onBlur={() => setIndFocused(false)}
          >
            <option value="" style={{ background: 'var(--surface)' }}>Select your industry…</option>
            {INDUSTRIES.map(i => <option key={i} value={i} style={{ background: 'var(--surface)' }}>{i}</option>)}
          </select>
        </div>
      </div>
    </div>
  )
}

function StepTeamSize({ teamSize, setTeamSize }: { teamSize: string; setTeamSize: (v: string) => void }) {
  return (
    <div>
      <h2 style={headingStyle}>How big is your team?</h2>
      <p style={subStyle}>We'll tailor some defaults to your setup.</p>
      <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 9 }}>
        {TEAM_SIZES.map(t => {
          const active = teamSize === t.id
          return (
            <button
              key={t.id}
              onClick={() => setTeamSize(t.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px',
                borderRadius: 11, cursor: 'pointer', textAlign: 'left', width: '100%',
                background: active ? 'color-mix(in srgb, var(--accent) var(--status-tint-fill), transparent)' : 'var(--surface)',
                border: `1px solid ${active ? 'color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' : 'var(--border-subtle)'}`,
                transition: 'all 0.15s',
              }}
            >
              <div style={{
                width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                border: `2px solid ${active ? 'var(--accent)' : 'var(--border-default)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {active && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }} />}
              </div>
              <span style={{ fontSize: 14, fontWeight: 500, color: active ? 'var(--ink-primary)' : 'var(--ink-secondary)' }}>
                {t.label}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function StepModules({ modules, setModules }: { modules: string[]; setModules: (v: string[]) => void }) {
  const toggle = (id: string) =>
    setModules(modules.includes(id) ? modules.filter(m => m !== id) : [...modules, id])
  return (
    <div>
      <h2 style={headingStyle}>Which modules do you need?</h2>
      <p style={subStyle}>All enabled by default — uncheck anything you won't use.</p>
      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 7 }}>
        {ALL_MODULES.map(mod => {
          const checked = modules.includes(mod.id)
          return (
            <button
              key={mod.id}
              onClick={() => toggle(mod.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 13, padding: '11px 13px',
                borderRadius: 10, cursor: 'pointer', textAlign: 'left', width: '100%',
                background: checked ? 'color-mix(in srgb, var(--accent) var(--status-tint-faint), transparent)' : 'var(--surface)',
                border: `1px solid ${checked ? 'color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' : 'var(--border-subtle)'}`,
                transition: 'all 0.15s',
              }}
            >
              <div style={{
                width: 17, height: 17, borderRadius: 5, flexShrink: 0,
                background: checked ? 'var(--accent)' : 'var(--border-subtle)',
                border: checked ? 'none' : '1px solid var(--border-default)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {checked && (
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: checked ? 'var(--ink-primary)' : 'var(--ink-secondary)' }}>{mod.id}</div>
                <div style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 1 }}>{mod.desc}</div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function StepBrand({ brandColor, setBrandColor }: { brandColor: string; setBrandColor: (v: string) => void }) {
  return (
    <div>
      <h2 style={headingStyle}>Make it yours</h2>
      <p style={subStyle}>Choose your brand color. You can update this anytime in Settings.</p>
      <div style={{ marginTop: 26, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <label style={labelStyle}>Primary color</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{ position: 'relative', width: 48, height: 48, borderRadius: 12, background: brandColor, boxShadow: `0 0 20px ${brandColor}66`, overflow: 'hidden', flexShrink: 0 }}>
              <input
                type="color"
                value={brandColor}
                onChange={e => setBrandColor(e.target.value)}
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
              />
            </div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)', letterSpacing: '0.04em' }}>{brandColor.toUpperCase()}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 2 }}>Click swatch to pick a custom color</div>
            </div>
            <div style={{ display: 'flex', gap: 7, marginLeft: 'auto' }}>
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => setBrandColor(c)}
                  style={{
                    width: 26, height: 26, borderRadius: 7, background: c, cursor: 'pointer', flexShrink: 0,
                    border: brandColor === c ? '2px solid white' : '2px solid transparent',
                    boxShadow: brandColor === c ? `0 0 8px ${c}` : 'none',
                    transition: 'all 0.15s',
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Color preview */}
        <div style={{ padding: '14px 16px', borderRadius: 12, background: 'var(--surface)', border: '1px solid var(--border-subtle)' }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--ink-tertiary)', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Preview</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <button style={{ padding: '7px 14px', borderRadius: 8, background: brandColor, border: 'none', color: 'var(--accent-ink)', fontSize: 12, fontWeight: 600, cursor: 'default' }}>
              Primary
            </button>
            <button style={{ padding: '7px 14px', borderRadius: 8, background: 'transparent', border: `1.5px solid ${brandColor}`, color: brandColor, fontSize: 12, fontWeight: 600, cursor: 'default' }}>
              Secondary
            </button>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${brandColor}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ width: 14, height: 14, borderRadius: 4, background: brandColor }} />
            </div>
          </div>
        </div>

        {/* Logo placeholder */}
        <div>
          <label style={labelStyle}>Logo <span style={{ color: 'var(--ink-tertiary)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
          <div style={{
            padding: '18px', borderRadius: 11, border: '1px dashed var(--border-default)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
            color: 'var(--ink-tertiary)', cursor: 'default',
          }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontSize: 12 }}>Logo upload coming soon</span>
          </div>
        </div>
      </div>
    </div>
  )
}

function StepSampleData({ sampleData, setSampleData }: { sampleData: 'sample' | 'empty'; setSampleData: (v: 'sample' | 'empty') => void }) {
  const options: Array<{ id: 'sample' | 'empty'; label: string; desc: string; badge?: string }> = [
    { id: 'sample', label: 'Load sample data', desc: 'Starts with demo contacts, deals, and conversations so you can explore the product right away.', badge: 'Recommended' },
    { id: 'empty', label: 'Start empty',       desc: 'A clean slate — bring your own data or add it manually.' },
  ]
  return (
    <div>
      <h2 style={headingStyle}>How do you want to start?</h2>
      <p style={subStyle}>You can always delete sample data later from Settings.</p>
      <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
        {options.map(opt => {
          const active = sampleData === opt.id
          return (
            <button
              key={opt.id}
              onClick={() => setSampleData(opt.id)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 14, padding: '15px 15px',
                borderRadius: 12, cursor: 'pointer', textAlign: 'left', width: '100%',
                background: active ? 'color-mix(in srgb, var(--accent) var(--status-tint-dim), transparent)' : 'var(--surface)',
                border: `1px solid ${active ? 'color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' : 'var(--border-subtle)'}`,
                transition: 'all 0.15s',
              }}
            >
              <div style={{
                marginTop: 2, width: 18, height: 18, borderRadius: '50%', flexShrink: 0,
                border: `2px solid ${active ? 'var(--accent)' : 'var(--border-default)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {active && <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)' }} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: active ? 'var(--ink-primary)' : 'var(--ink-secondary)' }}>{opt.label}</span>
                  {opt.badge && (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)', color: 'var(--accent-text)', letterSpacing: '0.04em' }}>
                      {opt.badge}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--ink-tertiary)', marginTop: 4, lineHeight: '1.5' }}>{opt.desc}</div>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function SummaryRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 14px', borderRadius: 10,
      background: 'var(--surface)', border: '1px solid var(--border-subtle)',
    }}>
      <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', fontWeight: 500 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'var(--ink-primary)', fontWeight: 600, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
    </div>
  )
}

function StepDone({ workspaceName, industry, teamSize, modules, brandColor, sampleData }: {
  workspaceName: string; industry: string; teamSize: string
  modules: string[]; brandColor: string; sampleData: 'sample' | 'empty'
}) {
  const teamLabel = TEAM_SIZES.find(t => t.id === teamSize)?.label ?? teamSize
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          style={{
            width: 56, height: 56, borderRadius: 16,
            background: 'var(--logo-bg-gradient)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 0 32px color-mix(in srgb, var(--accent) var(--status-tint-mute), transparent)',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </motion.div>
      </div>
      <h2 style={{ ...headingStyle, textAlign: 'center' }}>You're all set!</h2>
      <p style={{ ...subStyle, textAlign: 'center' }}>Here's a summary of your workspace setup.</p>
      <div style={{ marginTop: 20, display: 'flex', flexDirection: 'column', gap: 7 }}>
        <SummaryRow label="Workspace" value={workspaceName} />
        <SummaryRow label="Industry"  value={industry} />
        <SummaryRow label="Team size" value={teamLabel} />
        <SummaryRow label="Modules"   value={`${modules.length} of ${ALL_MODULES.length} enabled`} />
        <SummaryRow
          label="Brand color"
          value={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <span style={{ width: 12, height: 12, borderRadius: 3, background: brandColor, display: 'inline-block', boxShadow: `0 0 6px ${brandColor}` }} />
              {brandColor.toUpperCase()}
            </span>
          }
        />
        <SummaryRow label="Starting data" value={sampleData === 'sample' ? 'Sample data loaded' : 'Empty workspace'} />
      </div>
    </div>
  )
}

// ─── Main wizard ─────────────────────────────────────────────────────────────

function LoadingScreen() {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: 'var(--logo-bg-gradient)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0 24px color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <path d="M13 3L4 14h9l-2 7 9-11h-9l2-7z" fill="white" />
        </svg>
      </div>
    </div>
  )
}

export default function Onboarding() {
  const navigate = useNavigate()
  const { workspace, refreshWorkspace } = useAuth()

  const [step, setStep]           = useState(1)
  const [direction, setDirection] = useState(1)
  const [saving, setSaving]       = useState(false)

  const [workspaceName, setWorkspaceName] = useState(workspace?.name ?? '')
  const [industry, setIndustry]           = useState('')
  const [teamSize, setTeamSize]           = useState('')
  const [modules, setModules]             = useState<string[]>(ALL_MODULES.map(m => m.id))
  const [brandColor, setBrandColor]       = useState('#7c3aed')
  const [sampleData, setSampleData]       = useState<'sample' | 'empty'>('sample')

  if (!workspace) return <LoadingScreen />

  const go = (next: number) => {
    setDirection(next > step ? 1 : -1)
    setStep(next)
  }

  const canContinue = () => {
    if (step === 1) return workspaceName.trim().length > 0 && industry.length > 0
    if (step === 2) return teamSize.length > 0
    return true
  }

  const handleSubmit = async () => {
    setSaving(true)
    try {
      await supabase.from('workspaces').update({
        name: workspaceName,
        industry,
        team_size: teamSize,
        modules_enabled: modules,
        brand_color: brandColor,
        onboarding_completed_at: new Date().toISOString(),
      } as any).eq('id', workspace.id)

      if (sampleData === 'sample') await seedSampleData(workspace.id)

      await refreshWorkspace()
      navigate('/app', { replace: true })
    } catch (err) {
      console.error('Onboarding submit error:', err)
    } finally {
      setSaving(false)
    }
  }

  const renderStep = () => {
    switch (step) {
      case 1: return <StepWelcome workspaceName={workspaceName} setWorkspaceName={setWorkspaceName} industry={industry} setIndustry={setIndustry} />
      case 2: return <StepTeamSize teamSize={teamSize} setTeamSize={setTeamSize} />
      case 3: return <StepModules modules={modules} setModules={setModules} />
      case 4: return <StepBrand brandColor={brandColor} setBrandColor={setBrandColor} />
      case 5: return <StepSampleData sampleData={sampleData} setSampleData={setSampleData} />
      case 6: return <StepDone workspaceName={workspaceName} industry={industry} teamSize={teamSize} modules={modules} brandColor={brandColor} sampleData={sampleData} />
      default: return null
    }
  }

  const enabled = canContinue()

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--canvas)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: '24px 16px',
    }}>
      {/* Logo */}
      <div style={{ marginBottom: 28, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10,
          background: 'var(--logo-bg-gradient)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 0 24px color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M13 3L4 14h9l-2 7 9-11h-9l2-7z" fill="white" />
          </svg>
        </div>
        <span style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink-primary)', letterSpacing: '-0.04em' }}>Orkestra</span>
      </div>

      {/* Card */}
      <div style={{
        width: '100%', maxWidth: 520,
        background: 'var(--surface)', border: '1px solid var(--border-subtle)',
        borderRadius: 20, overflow: 'hidden',
      }}>
        {/* Progress bar */}
        <div style={{ height: 3, background: 'var(--border-subtle)' }}>
          <motion.div
            style={{ height: '100%', background: 'var(--accent)' }}
            animate={{ width: `${(step / 6) * 100}%` }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
          />
        </div>

        {/* Step header */}
        <div style={{ padding: '18px 28px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            Step {step} of 6
          </span>
          <div style={{ display: 'flex', gap: 5 }}>
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div
                key={i}
                style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: i <= step ? 'var(--accent)' : 'var(--border-default)',
                  transition: 'background 0.25s',
                }}
              />
            ))}
          </div>
        </div>

        {/* Animated step content */}
        <div style={{ padding: '22px 28px', overflow: 'hidden' }}>
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              {renderStep()}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation */}
        <div style={{ padding: '0 28px 26px', display: 'flex', gap: 9 }}>
          {step > 1 && (
            <button
              onClick={() => go(step - 1)}
              disabled={saving}
              style={{
                flexShrink: 0, padding: '11px 18px', borderRadius: 10, cursor: 'pointer',
                background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)',
                color: 'var(--ink-secondary)', fontSize: 14, fontWeight: 500,
              }}
            >
              Back
            </button>
          )}

          {step < 6 ? (
            <button
              onClick={() => go(step + 1)}
              disabled={!enabled}
              style={{
                flex: 1, padding: '11px 20px', borderRadius: 10, cursor: enabled ? 'pointer' : 'not-allowed',
                background: enabled ? 'var(--logo-bg-gradient)' : 'color-mix(in srgb, var(--accent) var(--status-tint-strong), transparent)',
                border: 'none', color: enabled ? 'var(--accent-ink)' : 'var(--ink-tertiary)',
                fontSize: 14, fontWeight: 600, transition: 'all 0.15s',
                boxShadow: enabled ? '0 0 18px color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' : 'none',
              }}
            >
              Continue
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={saving}
              style={{
                flex: 1, padding: '13px 20px', borderRadius: 10, cursor: saving ? 'not-allowed' : 'pointer',
                background: saving ? 'color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)' : 'var(--logo-bg-gradient)',
                border: 'none', color: 'var(--accent-ink)', fontSize: 15, fontWeight: 700,
                letterSpacing: '-0.01em', transition: 'all 0.15s',
                boxShadow: saving ? 'none' : '0 0 24px color-mix(in srgb, var(--accent) var(--status-tint-edge), transparent)',
              }}
            >
              {saving ? 'Setting up your workspace…' : 'Enter Orkestra'}
            </button>
          )}
        </div>
      </div>

      <div style={{ marginTop: 20, fontSize: 12, color: 'var(--border-default)' }}>
        Takes less than 2 minutes
      </div>
    </div>
  )
}
